import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { moveFile } from '../api/filesApi';
import { useMoveFolder } from './useMoveFolder';
import { useFileSelectionStore } from '../stores/fileSelectionStore';
import { folderContentsKeys } from './useFolderContents';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

export const BILLDA_MOVE_MIME = 'application/x-billda-move';

interface DragPayload {
  items: Array<{ id: string; type: 'file' | 'folder' }>;
}

/**
 * Build the drag payload. If the dragged item is in the selection,
 * include all selected items. Otherwise include only the dragged item.
 */
export function buildDragPayload(
  id: string,
  type: 'file' | 'folder'
): DragPayload {
  const store = useFileSelectionStore.getState();
  if (store.isSelected(id)) {
    return { items: store.getSelectedItems() };
  }
  return { items: [{ id, type }] };
}

export function useDragAndDrop(
  siteId: number,
  _currentFolderId: string | null
) {
  const NONE = '__NONE__' as const;
  const [dragOverTarget, setDragOverTarget] = useState<
    string | null | typeof NONE
  >(NONE);
  const [isDragging, setIsDragging] = useState(false);
  const draggedItemsRef = useRef<DragPayload['items']>([]);
  const queryClient = useQueryClient();
  const moveFolderMutation = useMoveFolder(siteId);
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const clearAll = useFileSelectionStore(state => state.clearAll);

  const getDragProps = useCallback(
    (item: { id: string; type: 'file' | 'folder' }) => ({
      draggable: true,
      onDragStart: (event: React.DragEvent) => {
        const payload = buildDragPayload(item.id, item.type);
        event.dataTransfer.setData(BILLDA_MOVE_MIME, JSON.stringify(payload));
        event.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
        draggedItemsRef.current = payload.items;

        // Create custom drag image
        const ghost = document.createElement('div');
        ghost.setAttribute('data-testid', 'drag-preview');
        ghost.style.cssText =
          'position:fixed;top:-1000px;left:-1000px;padding:8px 16px;background:#fff;border:1px solid #ccc;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:9999';
        ghost.textContent = `${payload.items.length} item${payload.items.length > 1 ? 's' : ''}`;
        document.body.appendChild(ghost);
        event.dataTransfer.setDragImage(ghost, 0, 0);
        requestAnimationFrame(() => document.body.removeChild(ghost));
      },
      onDragEnd: () => {
        setDragOverTarget(NONE);
        setIsDragging(false);
        draggedItemsRef.current = [];
      },
    }),
    []
  );

  const isDropAllowed = (targetFolderId: string | null) => {
    if (!targetFolderId) {
      return true;
    }
    return !draggedItemsRef.current.some(
      i => i.type === 'folder' && i.id === targetFolderId
    );
  };

  const getDropProps = useCallback(
    (targetFolderId: string | null) => ({
      onDragOver: (event: React.DragEvent) => {
        if (
          event.dataTransfer.types.includes(BILLDA_MOVE_MIME) &&
          isDropAllowed(targetFolderId)
        ) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDragOverTarget(targetFolderId);
        }
      },
      onDragLeave: (event: React.DragEvent) => {
        const relatedTarget = event.relatedTarget as Node | null;
        if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
          setDragOverTarget(prev => (prev === targetFolderId ? NONE : prev));
        }
      },
      onDrop: async (event: React.DragEvent) => {
        event.preventDefault();
        setDragOverTarget(NONE);

        if (!event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
          return; // OS file drop — handled elsewhere
        }

        if (!isDropAllowed(targetFolderId)) {
          return;
        }

        const raw = event.dataTransfer.getData(BILLDA_MOVE_MIME);
        if (!raw) {
          return;
        }

        const payload: DragPayload = JSON.parse(raw);
        const folders = payload.items.filter(i => i.type === 'folder');
        const files = payload.items.filter(i => i.type === 'file');

        let hadError = false;

        // Move folders
        for (const folder of folders) {
          try {
            await moveFolderMutation.mutateAsync({
              folderId: folder.id,
              targetParentFolderId: targetFolderId,
            });
          } catch (error: unknown) {
            hadError = true;
            if (error && typeof error === 'object' && 'response' in error) {
              const resp = (error as { response?: { status?: number } })
                .response;
              if (resp?.status === 400) {
                showSnackbar(
                  'Cannot move a folder into one of its own subfolders',
                  'error'
                );
              } else if (resp?.status === 409) {
                showSnackbar(
                  'A folder with this name already exists in the target',
                  'error'
                );
              }
            }
          }
        }

        // Move files
        for (const file of files) {
          try {
            await moveFile(file.id, targetFolderId ?? undefined);
          } catch (error: unknown) {
            hadError = true;
            if (error && typeof error === 'object' && 'response' in error) {
              const resp = (error as { response?: { status?: number } })
                .response;
              if (resp?.status === 409) {
                showSnackbar(
                  'A file with this name already exists in the target folder',
                  'error'
                );
              }
            }
          }
        }

        if (!hadError) {
          clearAll();
        }

        queryClient.invalidateQueries({
          queryKey: folderContentsKeys.all,
        });
      },
    }),
    [moveFolderMutation, queryClient, showSnackbar, clearAll]
  );

  const isDragOver = useCallback(
    (targetId: string | null) => isDragging && dragOverTarget === targetId,
    [isDragging, dragOverTarget]
  );

  return { getDragProps, getDropProps, isDragOver };
}
